import { request } from '@/api/request'

export interface Attachment {
  id: string
  ticketId: string
  messageId: string | null
  visibility: string
  fileName: string
  mimeType: string
  sizeBytes: number
  sha256?: string
  uploadedBy?: string
  uploadedAt: string
}

export interface PresignResult {
  storageKey: string
  upload: { url: string; method: string; headers: Record<string, string>; expiresAt: string }
}

export interface ConfirmAttachmentInput {
  storageKey: string
  fileName: string
  mimeType: string
  sizeBytes: number
  messageId?: string
  visibility?: 'customer' | 'internal'
}

const enc = encodeURIComponent

export const attachmentsApi = {
  list: (ticketId: string): Promise<Attachment[]> => request<Attachment[]>(`/tickets/${enc(ticketId)}/attachments`),

  presign: (ticketId: string, file: { fileName: string; mimeType: string; sizeBytes: number }): Promise<PresignResult> =>
    request<PresignResult>(`/tickets/${enc(ticketId)}/attachments/presign`, {
      method: 'POST',
      body: JSON.stringify(file),
    }),

  /**
   * PUT the bytes to the presigned URL. The URL is absolute and already signed,
   * so no bearer token or JSON handling: this is the one upload that bypasses
   * the API's JSON contract (it still goes through this module, never a component).
   */
  uploadBytes: async (presign: PresignResult, file: Blob): Promise<void> => {
    const res = await fetch(presign.upload.url, {
      method: presign.upload.method || 'PUT',
      headers: presign.upload.headers,
      body: file,
    })
    if (!res.ok) throw new Error(`Upload failed (${res.status})`)
  },

  confirm: (ticketId: string, input: ConfirmAttachmentInput): Promise<Attachment> =>
    request<Attachment>(`/tickets/${enc(ticketId)}/attachments`, { method: 'POST', body: JSON.stringify(input) }),

  get: (ticketId: string, attachmentId: string): Promise<Attachment & { download: { url: string } }> =>
    request<Attachment & { download: { url: string } }>(`/tickets/${enc(ticketId)}/attachments/${enc(attachmentId)}`),
}
